import { FC, KeyboardEvent, ReactNode, RefObject, useRef } from 'react';
import { Box, CircularProgress, IconButton, TextField, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { TooltipCard } from '@components/Tooltip';
import { useDirection } from '@hooks';
import {
  ALLOWED_DOCUMENT_CONTENT_TYPES,
  FileStatus,
  MAX_DOCUMENT_SIZE_BYTES,
} from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { getFileExtension, validateUploadFile } from '@libs/utils';
import { ArrowRightIcon, PlusIcon, StopIcon, XIcon } from '@phosphor-icons/react';
import { IconTelescope } from '@tabler/icons-react';

import {
  CHAT_COLUMN_MAX_WIDTH,
  CHAT_TOKENS,
  HERO_COMPOSER_MAX_WIDTH,
  MAX_VISIBLE_CHIPS,
} from '../constants';
import { type ChatAttachments } from '../hooks/useChatAttachments';
import { getFileIcon, getFileTypeLabel } from '../utils/fileUtils';

import { DeepResearchToggle } from './DeepResearchToggle';
import { FileChip, FileChipAction, FileChipOverflow, FileChipRow } from './FileChip';

/**
 * Where the composer is being rendered.
 *
 * - `hero`   — centred under the headline on a brand-new chat. Wrapped in the
 *              blue→green gradient ring, white field.
 * - `docked` — pinned under an open thread. Grey field, no ring.
 *
 * Both share every control, so the two placements read as one component in two
 * positions rather than two different inputs.
 */
export type ChatComposerPlacement = 'hero' | 'docked';

export interface ChatComposerProps {
  placement: ChatComposerPlacement;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** Deep Research (quality-rubric) mode for the next send. */
  deepResearchEnabled: boolean;
  onDeepResearchToggle: () => void;
  /** Abort an in-flight answer. Omit when the placement cannot stream (hero). */
  onStopGeneration?: () => void;
  isStreaming?: boolean;
  /** Hero only — the conversation is being created, so the send is in flight. */
  isSubmitting?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Document staged for the next send — see {@link useChatAttachments}. */
  attachments: ChatAttachments;
  /** The gradient shell — measured for the hero→dock shared-element transition. */
  shellRef?: RefObject<HTMLDivElement | null>;
  /** Exposes the text field so callers can focus it (e.g. after picking a suggestion). */
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Rendered under the composer (hero hint, error text). */
  footer?: ReactNode;
}

const ACCEPTED_MIME_TYPES = ALLOWED_DOCUMENT_CONTENT_TYPES;

/**
 * Allowed file extensions for chat attachments — the extension equivalent of
 * {@link ALLOWED_DOCUMENT_CONTENT_TYPES} (documents only, no images). Validated
 * before upload so unsupported files are rejected even when the OS file picker
 * lets them through (or on drag-drop).
 */
const CHAT_ALLOWED_FILE_TYPES = ['.pdf', '.txt', '.doc', '.docx', '.xls', '.xlsx'];
const CHAT_MAX_FILE_SIZE_MB = MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024);

export const ChatComposer: FC<ChatComposerProps> = observer(
  ({
    placement,
    value,
    onChange,
    onSend,
    deepResearchEnabled,
    onDeepResearchToggle,
    onStopGeneration,
    isStreaming = false,
    isSubmitting = false,
    disabled = false,
    autoFocus = false,
    attachments,
    shellRef,
    inputRef: externalInputRef,
    footer,
  }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const { isRtl } = useDirection();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const localInputRef = useRef<HTMLInputElement>(null);
    const inputRef = externalInputRef ?? localInputRef;

    const isHero = placement === 'hero';
    const { files } = attachments;
    const hasFile = files.length > 0;
    const isUploading = files.some((f) => f.status === FileStatus.UPLOADING);
    // A staged document is enough to send inside a thread — the question can be
    // implicit. Starting a conversation needs an actual question, since it also
    // becomes the thread's title.
    const isEmpty = isHero ? !value.trim() : !value.trim() && !hasFile;
    const isSendDisabled = disabled || isSubmitting || isUploading || (isEmpty && !isStreaming);
    const canAddFile = !hasFile && !isUploading && !disabled;

    // Staged documents win over the mode/placement default — they are the most
    // specific thing the user just did, and they change what a question means.
    const placeholder = hasFile
      ? files.length > 1
        ? t('chat.input.multiDocumentsPlaceholder', { count: files.length })
        : t('chat.input.checkDocumentsPlaceholder')
      : deepResearchEnabled
        ? isHero
          ? t('chat.new.form.deepPlaceholder')
          : t('chat.input.deepPlaceholder')
        : isHero
          ? t('chat.new.form.placeholder')
          : t('chat.input.placeholder');

    const handleSendClick = () => {
      if (isStreaming) {
        onStopGeneration?.();
        return;
      }
      if (isSendDisabled) return;
      onSend();
      inputRef.current?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      event.preventDefault();
      if (isSendDisabled || isStreaming) return;
      onSend();
      inputRef.current?.focus();
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const selected = Array.from(event.target.files ?? []);
      // Validate type/size before upload — the picker `accept` only hints at
      // allowed types and is bypassed by drag-drop / "all files".
      const validFiles: File[] = [];
      for (const file of selected) {
        const { valid, error } = validateUploadFile(file, {
          allowedFileTypes: CHAT_ALLOWED_FILE_TYPES,
          maxFileSizeMb: CHAT_MAX_FILE_SIZE_MB,
        });
        if (valid) {
          validFiles.push(file);
        } else if (error) {
          attachments.reportValidationError({
            type: error,
            fileName: file.name,
            fileExtension: getFileExtension(file.name),
          });
        }
      }
      if (validFiles.length > 0) {
        void attachments.addFiles(validFiles);
      }
      // Reset so the same file can be re-selected after removal
      event.target.value = '';
    };

    const attachButton = (
      <Box
        component="button"
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={!canAddFile}
        aria-label={t('chat.input.addFilesAndDocuments') as string}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: '50%',
          border: '1px solid',
          borderColor: CHAT_TOKENS.hairline,
          backgroundColor: '#fff',
          color: 'text.secondary',
          cursor: canAddFile ? 'pointer' : 'not-allowed',
          opacity: canAddFile ? 1 : 0.55,
          outline: 'none',
          transition: theme.transitions.create(
            ['background-color', 'border-color', 'transform'],
            { duration: 160 },
          ),
          '&:hover': canAddFile
            ? { backgroundColor: CHAT_TOKENS.hover, borderColor: '#DCDCDC', transform: 'translateY(-1px)' }
            : {},
          '&:active': canAddFile ? { transform: 'scale(.92)' } : {},
          '&:focus-visible': { outline: `2px solid ${theme.palette.accent.blue}`, outlineOffset: 2 },
        }}
      >
        <PlusIcon size={17} />
      </Box>
    );

    const messageField = (
      <TextField
        fullWidth
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        inputRef={inputRef}
        placeholder={placeholder as string}
        multiline
        minRows={1}
        maxRows={7}
        variant="outlined"
        slotProps={{ htmlInput: { dir: 'auto' } }}
        sx={{
          flex: 1,
          minWidth: 0,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent',
            px: 0.5,
            py: 1.25,
            alignItems: 'center',
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
          },
          '& .MuiInputBase-input': {
            p: 0,
            fontSize: '0.844rem',
            lineHeight: 1.5,
            color: 'text.primary',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            '&::placeholder': { color: CHAT_TOKENS.muted, opacity: 1 },
          },
        }}
      />
    );

    const sendButton = (
      <IconButton
        onClick={handleSendClick}
        disabled={isSendDisabled && !isStreaming}
        aria-label={isStreaming ? (t('chat.input.stop') as string) : (t('chat.input.send') as string)}
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: 'text.primary',
          color: 'common.white',
          transition:
            'background-color .18s ease, transform .14s cubic-bezier(.34,1.56,.64,1), box-shadow .18s ease',
          '&:hover': {
            backgroundColor: '#242424',
            transform: 'scale(1.07)',
            boxShadow: '0 4px 12px rgba(10,10,10,.22)',
          },
          '&:active': { transform: 'scale(.9)' },
          '&.Mui-disabled': {
            backgroundColor: 'text.primary',
            color: 'common.white',
            opacity: 0.35,
          },
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={17} thickness={5} sx={{ color: 'common.white' }} />
        ) : isStreaming ? (
          <StopIcon weight="fill" size={16} />
        ) : (
          <ArrowRightIcon
            size={17}
            style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }}
          />
        )}
      </IconButton>
    );

    const deepResearchPill = (
      <DeepResearchToggle
        enabled={deepResearchEnabled}
        onToggle={onDeepResearchToggle}
        disabled={disabled || isSubmitting}
      />
    );

    const attachmentTray = (
      <FileChipRow>
        {files.slice(0, MAX_VISIBLE_CHIPS).map((file) => {
          const isFileUploading = file.status === FileStatus.UPLOADING;
          const hasFailed = file.status === FileStatus.FAILED;

          return (
            <FileChip
              key={file.id}
              fileName={file.fileName}
              mimeType={file.mimeType}
              hasError={hasFailed}
              caption={
                isFileUploading ? (
                  <>
                    {file.progress}% {t('chat.input.uploading')}
                  </>
                ) : hasFailed ? (
                  t('chat.input.uploadFailed')
                ) : (
                  getFileTypeLabel(file.mimeType)
                )
              }
              action={
                isFileUploading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <CircularProgress size={13} thickness={6} />
                    <FileChipAction
                      size={22}
                      onClick={() => attachments.cancelFile(file.id)}
                      ariaLabel={t('chat.attachment.cancelUpload') as string}
                    >
                      <XIcon size={13} />
                    </FileChipAction>
                  </Box>
                ) : (
                  <FileChipAction
                    size={22}
                    onClick={() => attachments.removeFile(file.id)}
                    ariaLabel={t('chat.attachment.remove') as string}
                  >
                    <XIcon size={13} />
                  </FileChipAction>
                )
              }
            />
          );
        })}

        {files.length > MAX_VISIBLE_CHIPS && (
          <FileChipOverflow count={files.length - MAX_VISIBLE_CHIPS} />
        )}

        {files.length > 1 && (
          <Box
            component="button"
            type="button"
            onClick={attachments.clear}
            sx={{
              marginInlineStart: 'auto',
              border: 'none',
              backgroundColor: 'transparent',
              borderRadius: 2,
              padding: '5px 6px',
              fontFamily: 'inherit',
              fontSize: '0.688rem',
              color: CHAT_TOKENS.muted,
              cursor: 'pointer',
              transition: 'background-color .16s ease, color .16s ease',
              '&:hover': { backgroundColor: '#FDECEC', color: 'error.main' },
            }}
          >
            {t('chat.attachment.clearAll')}
          </Box>
        )}
      </FileChipRow>
    );

    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: isHero ? HERO_COMPOSER_MAX_WIDTH : CHAT_COLUMN_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        {/* Deep Research is a mode, not a one-off — say so above the field it applies to. */}
        {deepResearchEnabled && !isHero && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.875,
              width: 'max-content',
              mx: 'auto',
              mb: 1,
              px: 1.375,
              py: 0.625,
              borderRadius: '999px',
              border: '1px solid #DCE9FC',
              backgroundColor: CHAT_TOKENS.deepWash,
              color: CHAT_TOKENS.deepInk,
              fontSize: '0.719rem',
              '@keyframes composerNoteIn': {
                from: { opacity: 0, transform: 'translateY(16px)' },
                to: { opacity: 1, transform: 'none' },
              },
              animation: 'composerNoteIn .3s ease both',
            }}
          >
            <IconTelescope size={14} stroke={2} style={{ color: theme.palette.accent.blue }} />
            {t('chat.input.deepResearchOnNote')}
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_MIME_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />

        {/* The hero's send is also the conversation's creation, so say plainly when
            the attachment gets read. */}
        {hasFile && isHero && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.875,
              width: 'max-content',
              mx: 'auto',
              mb: 1,
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              border: `1px solid ${CHAT_TOKENS.hairline}`,
              backgroundColor: CHAT_TOKENS.surface,
              color: 'text.secondary',
              fontSize: '0.688rem',
              '@keyframes composerNoteIn': {
                from: { opacity: 0, transform: 'translateY(16px)' },
                to: { opacity: 1, transform: 'none' },
              },
              animation: 'composerNoteIn .3s ease both',
            }}
          >
            <Box sx={{ display: 'flex', color: 'accent.green' }}>{getFileIcon(undefined, 14)}</Box>
            {t('chat.new.attachedNote')}
          </Box>
        )}

        {/* Docked, the tray sits above the shell; in the hero it rides inside the
            gradient ring so the ring frames the question and its evidence together. */}
        {hasFile && !isHero && attachmentTray}

        <Box
          ref={shellRef}
          sx={{
            width: '100%',
            ...(isHero
              ? {
                  p: '3px',
                  borderRadius: '22px',
                  background: 'linear-gradient(90deg,#D7EEE3,#DCE7F8)',
                }
              : {}),
          }}
        >
          {hasFile && isHero && <Box sx={{ px: '5px', pt: '5px' }}>{attachmentTray}</Box>}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              p: 1,
              borderRadius: '20px',
              backgroundColor: isHero ? '#fff' : CHAT_TOKENS.field,
            }}
          >
            <TooltipCard
              variant="dark"
              placement="top"
              description={t('chat.input.addFilesAndDocuments') as string}
            >
              {/* Wrapper keeps the tooltip reachable while the button is disabled. */}
              <span style={{ display: 'inline-flex' }}>{attachButton}</span>
            </TooltipCard>

            {/* The hero leads with the question and offers depth after it; an open
                thread already has depth in play, so the pill comes first. */}
            {isHero ? (
              <>
                {messageField}
                {deepResearchPill}
              </>
            ) : (
              <>
                {deepResearchPill}
                {messageField}
              </>
            )}

            {sendButton}
          </Box>
        </Box>

        {footer}
      </Box>
    );
  },
);
